import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, CheckCircle, AlertCircle, UserX } from 'lucide-react';
import type { LoginStep, LoginPageProps, ApiResponse } from '../../types';

const LoginPage: React.FC<LoginPageProps> = ({ onSwitchToSignUp }) => {
  const { sendOTP, verifyOTP } = useAuth();
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [showNotFoundModal, setShowNotFoundModal] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setEmail(value);
    setIsValidEmail(validateEmail(value));
    // Reset modal if user changes email
    if (showNotFoundModal) setShowNotFoundModal(false);
  };

  const handleSendOTP = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!isValidEmail) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    setLoading(true);
    const result: ApiResponse<any> = await sendOTP(email);
    setLoading(false);

    if (result.success) {
      setStep('otp');
      toast.success('Código enviado a tu email', {
        description: 'Revisa tu bandeja de entrada y spam'
      });
    } else {
      // Debug: log the exact error
      console.log('🔴 OTP Error:', result.error);

      // Check for user not found scenarios (various error message formats)
      const errorLower = (result.error || '').toLowerCase();
      const isUserNotFound =
        result.code === 'user_not_found' ||
        errorLower.includes('user not found') ||
        errorLower.includes('usuario no encontrado') ||
        errorLower.includes('not found') ||
        errorLower.includes('404') ||
        errorLower.includes('failed to send otp'); // Fallback error when 404 is returned

      if (isUserNotFound) {
        // Show confirmation modal instead of toast
        setShowNotFoundModal(true);
      } else {
        toast.error('Error al enviar código', {
          description: result.error || 'Error desconocido'
        });
      }
    }
  };

  const handleNotFoundNo = (): void => {
    setShowNotFoundModal(false);
    setEmail('');
    setIsValidEmail(false);
  };

  const handleNotFoundYes = (): void => {
    setShowNotFoundModal(false);
    onSwitchToSignUp();
  };

  const handleVerifyOTP = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (otpCode.length !== 6) {
      toast.error('El código debe tener 6 dígitos');
      return;
    }

    setLoading(true);
    const result: ApiResponse<{ user: any }> = await verifyOTP(email, otpCode);
    setLoading(false);

    if (result.success) {
      toast.success('¡Bienvenido!', {
        description: 'Has iniciado sesión exitosamente'
      });
      // Navigation is handled by App.js based on auth state
    } else {
      toast.error('Código incorrecto', {
        description: result.error || 'Código inválido'
      });
      setOtpCode(''); // Clear the OTP input
    }
  };

  const handleBack = (): void => {
    setStep('email');
    setOtpCode('');
  };

  const handleResendOTP = async (): Promise<void> => {
    setLoading(true);
    const result: ApiResponse<any> = await sendOTP(email);
    setLoading(false);

    if (result.success) {
      toast.success('Nuevo código enviado');
    } else {
      toast.error('Error al reenviar código');
    }
  };

  const handleOTPChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F7F7] via-blue-50 to-indigo-50 flex items-center justify-center p-3 sm:p-4 lg:p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] px-6 sm:px-8 lg:px-10 py-6 sm:py-8 text-center">
          <div className="flex items-center justify-center mx-auto mb-4">
            <div className="bg-gray-50 rounded-2xl p-4 sm:p-5 lg:p-6 shadow-lg">
              <img
                src="/assets/logo_reveca_def.png"
                alt="Reveca Logo"
                className="w-24 h-10 sm:w-32 sm:h-10 lg:w-40 lg:h-10 object-contain"
              />
            </div>
          </div>
          <p className="text-blue-100 text-sm sm:text-base">
            {step === 'email'
              ? 'Inicia sesión con tu cuenta registrada'
              : 'Verifica el código enviado a tu email'
            }
          </p>
        </div>

        {/* Content */}
        <div className="px-6 sm:px-8 lg:px-10 py-6 sm:py-8 lg:py-10">
          {step === 'email' ? (
            <form onSubmit={handleSendOTP} className="space-y-5 sm:space-y-6 lg:space-y-7">
              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  Email de tu cuenta
                </label>
                <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                  Si ya tienes una cuenta registrada, ingresa tu email aquí
                </p>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="tu@email.com"
                    className={`w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 border-2 rounded-lg sm:rounded-xl focus:outline-none transition-all duration-200 text-base sm:text-lg ${email && isValidEmail
                      ? 'border-green-300 bg-green-50'
                      : email && !isValidEmail
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 focus:border-[#1CB3B3] focus:bg-[#E8F7F7]'
                      }`}
                    disabled={loading}
                  />
                  {email && (
                    <div className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center">
                      {isValidEmail ? (
                        <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
                {email && !isValidEmail && (
                  <p className="text-red-600 text-xs sm:text-sm mt-1">
                    Ingresa un email válido
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isValidEmail || loading}
                className="w-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 text-base sm:text-lg shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Enviando código...</span>
                  </>
                ) : (
                  <>
                    <span>Iniciar Sesión</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-5 sm:space-y-6 lg:space-y-7">
              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2 sm:mb-3">
                  Código de verificación
                </label>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-5">
                  Hemos enviado un código de 6 dígitos a <strong>{email}</strong>
                </p>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={handleOTPChange}
                    placeholder="123456"
                    className="w-full pl-10 sm:pl-12 pr-4 sm:pr-6 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:border-[#1CB3B3] focus:bg-[#E8F7F7] text-center text-lg sm:text-xl font-mono tracking-widest transition-all duration-200"
                    maxLength={6}
                    disabled={loading}
                  />
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                  El código expira en 10 minutos
                </p>
              </div>

              <button
                type="submit"
                disabled={otpCode.length !== 6 || loading}
                className="w-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 text-base sm:text-lg shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Verificando...</span>
                  </>
                ) : (
                  <>
                    <span>Verificar código</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>

              <div className="flex flex-col space-y-3 sm:space-y-4">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-sm sm:text-base text-[#1CB3B3] hover:text-[#0D7377] disabled:opacity-50 font-medium"
                >
                  ¿No recibiste el código? Reenviar
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading}
                  className="text-sm sm:text-base text-gray-600 hover:text-gray-700 disabled:opacity-50 font-medium"
                >
                  ← Cambiar email
                </button>
              </div>
            </form>
          )}

          {/* Switch to sign up */}
          <div className="mt-2 pt-4 border-t border-gray-100 text-center">
            <p className="text-sm sm:text-base text-gray-500 mb-2 font-medium">
              Si no tienes una cuenta:
            </p>
            <button
              type="button"
              onClick={onSwitchToSignUp}
              disabled={loading}
              className="w-full bg-white border-2 border-[#1CB3B3] text-[#1CB3B3] py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl font-bold hover:bg-[#E8F7F7] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 text-base sm:text-lg shadow-md group"
            >
              <span>Regístrate aquí</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Footer */}

      </div>

      {/* Modal de Usuario No Encontrado */}
      {showNotFoundModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 animate-in fade-in zoom-in duration-200">
            <div className="flex flex-col items-center text-center">
              <div className="bg-amber-100 rounded-full p-4 mb-4">
                <UserX className="h-10 w-10 sm:h-12 sm:w-12 text-amber-600" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Cuenta no encontrada
              </h3>
              <p className="text-gray-600 text-sm sm:text-base mb-2">
                El email <strong className="text-gray-800">{email}</strong> no está registrado en nuestro sistema.
              </p>
              <p className="text-gray-600 text-sm sm:text-base mb-6">
                ¿Te gustaría crear una cuenta nueva?
              </p>

              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <button
                  type="button"
                  onClick={handleNotFoundNo}
                  className="flex-1 px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                >
                  No, volver
                </button>
                <button
                  type="button"
                  onClick={handleNotFoundYes}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white rounded-xl font-medium hover:opacity-90 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Sí, registrarme
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;